import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight, FiRefreshCw } from 'react-icons/fi';
import StatusBadge from '../monitoring/StatusBadge';
import {
    ReadinessCheck,
    SingleCheckResponse,
    recheckTenantReadinessCheck,
} from '../../api/tenantApi';

/** Display text for the five readiness states. The badge colours live in StatusBadge. */
const STATUS_LABELS: Record<string, string> = {
    ok: 'OK',
    fail: 'FAILED',
    blocked: 'BLOCKED',
    warn: 'WARN',
    unknown: 'UNKNOWN',
};

const Spinner: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={`animate-spin ${className ?? 'h-3 w-3'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
);

/**
 * Renders one value of a check's free-form `detail` object. Scalars render inline;
 * objects and arrays render as indented JSON. `detail` is arbitrary server data, so
 * this must never assume a shape and must never throw on nesting or on null.
 */
const DetailValue: React.FC<{ value: any }> = ({ value }) => {
    if (value === null || value === undefined) {
        return <span className="text-gray-400 dark:text-gray-500 italic">null</span>;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return <span className="font-mono break-all">{String(value)}</span>;
    }
    let text: string;
    try {
        text = JSON.stringify(value, null, 2) ?? String(value);
    } catch {
        // Circular or otherwise unserialisable — degrade, never crash the panel.
        text = String(value);
    }
    return (
        <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-gray-50 dark:bg-gray-900 px-2 py-1 font-mono text-[11px] text-gray-700 dark:text-gray-300">
            {text}
        </pre>
    );
};

const DetailBlock: React.FC<{ detail: Record<string, any> | null | undefined }> = ({ detail }) => {
    const entries = detail && typeof detail === 'object' ? Object.entries(detail) : [];
    if (entries.length === 0) {
        return (
            <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                No further detail reported for this check.
            </div>
        );
    }
    return (
        <dl className="space-y-1.5">
            {entries.map(([key, value]) => (
                <div key={key} className="text-xs">
                    <dt className="font-medium text-gray-600 dark:text-gray-300">{key}</dt>
                    <dd className="ml-3 text-gray-700 dark:text-gray-400">
                        <DetailValue value={value} />
                    </dd>
                </div>
            ))}
        </dl>
    );
};

interface ReadinessCheckRowProps {
    tenantId: string;
    check: ReadinessCheck;
    /** id -> label, so a `blockedBy` id can be named rather than shown raw. */
    labelsById: Record<string, string>;
    /**
     * The single-check endpoint returns the FULL merged aggregate as well as the one row,
     * because summary/canActivate/blocking are recomputed over the merged set server-side.
     * Handing the whole response up keeps the footer honest after a single-row re-check.
     */
    onResult: (response: SingleCheckResponse) => void;
    onError: (message: string) => void;
    /** True while a panel-wide refresh is in flight — per-row actions stand down. */
    busy?: boolean;
}

const ReadinessCheckRow: React.FC<ReadinessCheckRowProps> = ({
    tenantId, check, labelsById, onResult, onError, busy,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [rechecking, setRechecking] = useState(false);

    const isBlocked = check.status === 'blocked';

    const handleRecheck = async () => {
        setRechecking(true);
        try {
            const response = await recheckTenantReadinessCheck(tenantId, check.id);
            onResult(response);
        } catch (err: any) {
            onError(
                err?.response?.data?.error
                ?? err?.message
                ?? `Re-check of "${check.label}" failed.`
            );
        } finally {
            setRechecking(false);
        }
    };

    const blockerNames = (check.blockedBy ?? []).map(id => labelsById[id] ?? id);

    return (
        <div className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
            <div className="flex items-start gap-3 px-3 py-2">
                <button
                    type="button"
                    onClick={() => setExpanded(e => !e)}
                    aria-expanded={expanded}
                    aria-label={expanded ? `Hide detail for ${check.label}` : `Show detail for ${check.label}`}
                    className="mt-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                    {expanded ? <FiChevronDown className="h-4 w-4" /> : <FiChevronRight className="h-4 w-4" />}
                </button>

                <div className="w-24 shrink-0 pt-0.5">
                    <StatusBadge status={check.status} label={STATUS_LABELS[check.status] ?? check.status} />
                </div>

                <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {check.label}
                        {!check.required && (
                            <span className="ml-2 text-[10px] font-normal uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                advisory
                            </span>
                        )}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 break-words">
                        {check.summary}
                    </div>
                    {isBlocked && (
                        <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                            Blocked by: <span className="font-medium">{blockerNames.length ? blockerNames.join(', ') : 'an upstream check'}</span>
                            {' — '}fix that first; re-checking this row on its own would only repeat the same failure.
                        </div>
                    )}
                </div>

                <div className="shrink-0 pt-0.5">
                    {/*
                      BLOCKED rows offer NO action at all. That is the mechanism that stops the
                      panel lying when several failures share one root cause — the operator is
                      pointed at the blocker instead of being handed a button that cannot help.
                      No remediation buttons anywhere in this phase: probes only.
                    */}
                    {!isBlocked && (
                        <button
                            type="button"
                            onClick={handleRecheck}
                            disabled={rechecking || busy}
                            title={`Re-probe "${check.label}" only`}
                            className="inline-flex items-center gap-1.5 rounded border border-gray-300 dark:border-gray-600 px-2 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                            {rechecking ? <Spinner /> : <FiRefreshCw className="h-3 w-3" />}
                            {rechecking ? 'Checking…' : 'Re-check'}
                        </button>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30 px-3 py-2 pl-10">
                    <DetailBlock detail={check.detail} />
                </div>
            )}
        </div>
    );
};

export default ReadinessCheckRow;
